import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
} from '@mui/material';
import {
  WhatsApp,
  QrCode,
  CheckCircle,
  Error,
  Refresh,
  Stop,
  PlayArrow,
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { whatsappApi } from '../api';

interface SessionStatus {
  isConnected: boolean;
  needsQR: boolean;
  qrCode?: string;
  phoneNumber?: string;
  lastConnected?: string;
  error?: string;
  maxQRReached?: boolean;
}

const WhatsAppPage: React.FC = () => {
  const [sessionStatus, setSessionStatus] = useState<SessionStatus>({
    isConnected: false,
    needsQR: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrDialogManuallyClosed, setQrDialogManuallyClosed] = useState(false);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);

  const fetchSessionStatus = async () => {
    try {
      const response = await whatsappApi.getSessionStatus();
      
      if (response.data.success) {
        const newStatus = response.data.data;
        setSessionStatus(newStatus);
        
        // If connected, close QR dialog automatically and show success message
        if (newStatus.isConnected) {
          if (qrDialogOpen) {
            setQrDialogOpen(false);
            setSuccess('WhatsApp conectado com sucesso!');
          }
          setQrDialogManuallyClosed(false); // Reset manual close flag
          setError(null); // Clear any errors
        } else {
          // If QR code is available and dialog is not open, show it
          // But only if the user hasn't manually closed it
          if (newStatus.needsQR && newStatus.qrCode && !qrDialogOpen && !qrDialogManuallyClosed) {
            setQrDialogOpen(true);
          }
        }
        
        // Clear any previous errors if session is working
        if (newStatus.isConnected || (newStatus.needsQR && newStatus.qrCode)) {
          setError(null);
        }
      }
    } catch (error) {
      console.error('Error fetching session status:', error);
    }
  };

  const startSession = async (forceReset: boolean = false) => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    setQrDialogManuallyClosed(false); // Reset manual close flag when starting new session

    try {
      const response = await whatsappApi.startSession(forceReset);
      
      if (response.data.success) {
        setSessionStatus(response.data.data);
        setSuccess(response.data.message);
        
        // Start polling for status updates
        startPolling();
        
        // If already connected, don't show QR dialog
        if (response.data.data.isConnected) {
          setQrDialogOpen(false);
          setSuccess('WhatsApp já está conectado!');
        } else if (response.data.data.needsQR && response.data.data.qrCode) {
          // Show QR dialog if needed and not connected
          setQrDialogOpen(true);
        }
      } else {
        setError(response.data.message);
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Erro ao iniciar sessão do WhatsApp');
      console.error('Error starting session:', error);
    } finally {
      setLoading(false);
    }
  };

  const stopSession = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await whatsappApi.stopSession();
      
      if (response.data.success) {
        setSessionStatus({
          isConnected: false,
          needsQR: true,
        });
        setSuccess(response.data.message);
        setQrDialogOpen(false);
        stopPolling();
      } else {
        setError(response.data.message);
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Erro ao encerrar sessão do WhatsApp');
      console.error('Error stopping session:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetSession = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    setQrDialogManuallyClosed(false); // Reset manual close flag when resetting session

    try {
      const response = await whatsappApi.resetSession();
      
      if (response.data.success) {
        setSessionStatus({
          isConnected: false,
          needsQR: true,
        });
        setSuccess(response.data.message);
        setQrDialogOpen(false);
        stopPolling();
      } else {
        setError(response.data.message);
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Erro ao resetar sessão do WhatsApp');
      console.error('Error resetting session:', error);
    } finally {
      setLoading(false);
    }
  };

  const startPolling = () => {
    stopPolling(); // Clear any existing interval
    const interval = setInterval(fetchSessionStatus, 3000); // Poll every 3 seconds
    setPollingInterval(interval);
  };

  const stopPolling = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  };

  const refreshStatus = () => {
    fetchSessionStatus();
    setError(null);
    setSuccess(null);
    setQrDialogManuallyClosed(false); // Reset manual close flag when refreshing
  };

  const handleQrDialogClose = () => {
    setQrDialogOpen(false);
    setQrDialogManuallyClosed(true); // Mark as manually closed
  };

  const handleShowQrCode = () => {
    setQrDialogOpen(true);
    setQrDialogManuallyClosed(false); // Reset manual close flag when explicitly showing QR
  };

  useEffect(() => {
    fetchSessionStatus();
    
    // Cleanup on unmount
    return () => {
      stopPolling();
    };
  }, []);

  // Auto-dismiss success/error messages
  useEffect(() => {
    if (success || error) {
      const timeout = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timeout);
    }
  }, [success, error]);

  // Auto-close QR dialog when connected
  useEffect(() => {
    if (sessionStatus.isConnected && qrDialogOpen) {
      setQrDialogOpen(false);
      setQrDialogManuallyClosed(false);
      if (!success) { // Only show success if not already showing one
        setSuccess('WhatsApp conectado com sucesso!');
      }
    }
  }, [sessionStatus.isConnected, qrDialogOpen, success]);

  const getStatusColor = () => {
    if (sessionStatus.isConnected) return 'success';
    if (sessionStatus.needsQR) return 'warning';
    return 'error';
  };

  const getStatusText = () => {
    if (sessionStatus.isConnected) return 'Conectado';
    if (sessionStatus.needsQR) return 'Precisa escanear QR';
    return 'Desconectado';
  };

  const getStatusIcon = () => {
    if (sessionStatus.isConnected) return <CheckCircle />;
    if (sessionStatus.needsQR) return <QrCode />;
    return <Error />;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, color: "#2c3e50", fontWeight: 600 }}>
        <WhatsApp sx={{ mr: 2, fontSize: 40 }} />
        Gerenciamento WhatsApp
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        {/* Session Status Card */}
        <Box sx={{ flex: '1 1 400px', minWidth: '400px' }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                  Status da Sessão
                </Typography>
                <Button
                  size="small"
                  onClick={refreshStatus}
                  startIcon={<Refresh />}
                  disabled={loading}
                >
                  Atualizar
                </Button>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                {getStatusIcon()}
                <Chip
                  label={getStatusText()}
                  color={getStatusColor()}
                  sx={{ ml: 2 }}
                />
              </Box>

              {sessionStatus.phoneNumber && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>Número:</strong> +{sessionStatus.phoneNumber}
                </Typography>
              )}

              {sessionStatus.lastConnected && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  <strong>Última conexão:</strong>{' '}
                  {new Date(sessionStatus.lastConnected).toLocaleString('pt-BR')}
                </Typography>
              )}

              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                {!sessionStatus.isConnected ? (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<PlayArrow />}
                    onClick={() => startSession(true)}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={20} /> : 'Iniciar Sessão'}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<Stop />}
                    onClick={stopSession}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={20} /> : 'Encerrar Sessão'}
                  </Button>
                )}

                {sessionStatus.needsQR && sessionStatus.qrCode && (
                  <Button
                    variant="outlined"
                    startIcon={<QrCode />}
                    onClick={handleShowQrCode}
                  >
                    Mostrar QR Code
                  </Button>
                )}

                {sessionStatus.maxQRReached && (
                  <Button
                    variant="contained"
                    color="warning"
                    startIcon={<Refresh />}
                    onClick={resetSession}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={20} /> : 'Resetar Sessão'}
                  </Button>
                )}
              </Box>

              {/* Error Alert */}
              {sessionStatus.error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    {sessionStatus.error}
                  </Typography>
                  {sessionStatus.maxQRReached && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Clique em "Resetar Sessão" para tentar novamente.
                    </Typography>
                  )}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Instructions Card */}
        <Box sx={{ flex: '1 1 400px', minWidth: '400px' }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Como usar
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>1. Iniciar Sessão:</strong> Clique em "Iniciar Sessão" para começar.
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>2. Escanear QR Code:</strong> Use o WhatsApp do seu telefone para escanear o código QR que aparecerá.
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>3. Conexão Automática:</strong> Uma vez conectado, o sistema enviará mensagens automaticamente.
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>4. Gerenciar Sessão:</strong> Você pode encerrar a sessão a qualquer momento.
              </Typography>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Importante:</strong> Mantenha o WhatsApp conectado no seu telefone para que as mensagens sejam enviadas corretamente.
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* QR Code Dialog */}
      <Dialog
        open={qrDialogOpen}
        onClose={handleQrDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <QrCode sx={{ mr: 2 }} />
            Escaneie o Código QR
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            {sessionStatus.qrCode ? (
              <Paper sx={{ p: 3, display: 'inline-block' }}>
                <QRCodeSVG
                  value={sessionStatus.qrCode}
                  size={256}
                  level="M"
                  includeMargin
                />
              </Paper>
            ) : (
              <CircularProgress />
            )}
            
            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
              1. Abra o WhatsApp no seu telefone
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              2. Vá em Menu {' > '} Dispositivos conectados
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              3. Toque em "Conectar um dispositivo"
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              4. Aponte a câmera para este código QR
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleQrDialogClose}>
            Fechar
          </Button>
          <Button onClick={refreshStatus} variant="outlined">
            Atualizar Status
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WhatsAppPage; 